import { Alert } from 'antd'
import React from 'react'
import styled from 'styled-components/macro'
import { getHarbourColumnMeta } from 'src/constants/harbour'
import HarbourCard from './HarbourCard'

/**
 * One Harbour work column. Cards and buttons, not drag-as-primary.
 * Needs you never grows a page 2.
 */
const HarbourColumn = ({
  column,
  cards = [],
  caption = null,
  detail = null,
  emptyCopy = 'Nothing here',
  loading = false,
  error = null,
  skeleton = null,
  now,
  startBusyVenueId = null,
  onRetry,
  onSelectVenue,
  onStartWork,
  onAskSales,
  onAskProduct,
}) => {
  const meta = getHarbourColumnMeta(column) || { label: column }
  const showSkeleton =
    Boolean(skeleton) && loading && cards.length === 0 && !error

  return (
    <Column
      data-testid={`harbour-column-${column}`}
      data-column={column}
      style={{
        '--harbour-column-accent': meta.columnAccent || '#5B8A72',
        '--harbour-column-bg': meta.columnBackground || '#F4F8F6',
      }}
    >
      <Header>
        <Heading>
          <Label>{meta.label || column}</Label>
          {meta.subtitle ? <Subtitle>{meta.subtitle}</Subtitle> : null}
          {caption ? (
            <Caption data-testid={`harbour-column-caption-${column}`}>
              {caption}
            </Caption>
          ) : null}
          {detail ? (
            <CaptionDetail
              data-testid={`harbour-column-caption-detail-${column}`}
            >
              {detail}
            </CaptionDetail>
          ) : null}
        </Heading>
        <Count data-testid={`harbour-column-count-${column}`}>
          {cards.length}
        </Count>
      </Header>

      {error ? (
        <Alert
          type='error'
          showIcon
          message={error}
          action={
            onRetry ? (
              <button
                type='button'
                onClick={onRetry}
                data-testid={`harbour-column-retry-${column}`}
              >
                Retry
              </button>
            ) : null
          }
        />
      ) : null}

      {showSkeleton ? skeleton : null}

      {!showSkeleton && !error && cards.length === 0 ? (
        <Empty data-testid={`harbour-column-empty-${column}`}>
          {emptyCopy}
        </Empty>
      ) : null}

      {cards.length > 0 ? (
        <CardList>
          {cards.map(card => (
            <HarbourCard
              key={card.venueId}
              card={card}
              now={now}
              startBusy={startBusyVenueId === card.venueId}
              onSelectVenue={onSelectVenue}
              onStartWork={onStartWork}
              onAskSales={onAskSales}
              onAskProduct={onAskProduct}
            />
          ))}
        </CardList>
      ) : null}
    </Column>
  )
}

const Column = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 16.5rem;
  padding: 10px;
  border-top: 3px solid var(--harbour-column-accent);
  background: var(--harbour-column-bg);
  border-radius: 8px;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const Heading = styled.div`
  min-width: 0;
`

const Label = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
`

const Subtitle = styled.p`
  margin: 2px 0 0;
  font-size: 11px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const Caption = styled.p`
  margin: 4px 0 0;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const CaptionDetail = styled.p`
  margin: 2px 0 0;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const Count = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 70vh;
  overflow: auto;
`

const Empty = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

export default HarbourColumn
